import React, { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  decreaseZoomLevel,
  increaseZoomLevel,
  setZoomValue,
  calculateZoomMultipliers,
  zoomLevelStore
} from '../store/zoomLevel';
import IconButton from '@/components/atoms/Button/IconButton';
import { roundToNDecimalPlaces } from '@/libs/utils';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import '@/Pages/Clip/ClipPlayer/ClipSpeakersTimeline/SpeakersTimelineZoom/index';

function ZoomRange() {
  const { zoomLevel, baseZoomLevel } = useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);
  const zoomMultipliers = useMemo(() => calculateZoomMultipliers(baseZoomLevel), [baseZoomLevel]);

  const onZoomChange = useCallback(
    e => {
      const newIndex = +e.target.value;
      setZoomValue(zoomMultipliers[newIndex] * baseZoomLevel);
      EventBus.dispatch(CustomEvents.ZoomValueChangedFromRangeSlider);
    },
    [baseZoomLevel, zoomMultipliers]
  );

  const onZoomIncrease = useCallback(() => {
    increaseZoomLevel();
  }, []);

  const onZoomDecrease = useCallback(() => {
    decreaseZoomLevel();
  }, []);

  const currentZoomIndex = useMemo(() => {
    return zoomMultipliers.findIndex(
      level => roundToNDecimalPlaces(level * baseZoomLevel, 2) >= roundToNDecimalPlaces(zoomLevel, 2)
    );
  }, [baseZoomLevel, zoomLevel, zoomMultipliers]);

  return (
    <div className="flex items-center justify-center gap-2">
      <IconButton
        icon="IconZoomOut"
        onClick={onZoomDecrease}
        iconClassName="cursor-pointer"
        buttonClassName="w-9 h-9 border-none"
        variation="checkbox"
        trackingId="timeline-zoom-out"
        onMouseDown={e => e.stopPropagation()}
      />
      <input
        type="range"
        min={0}
        max={zoomMultipliers.length - 1}
        value={currentZoomIndex}
        step={1}
        onChange={onZoomChange}
        className="h-1 w-32 cursor-pointer appearance-none rounded-xl bg-slate-300"
        onMouseDown={e => e.stopPropagation()}
        data-testid="timeline-zoom-range"
      />
      <IconButton
        icon="IconZoomIn"
        onClick={onZoomIncrease}
        iconClassName="cursor-pointer"
        buttonClassName="w-9 h-9 border-none"
        variation="checkbox"
        trackingId="timeline-zoom-in"
        onMouseDown={e => e.stopPropagation()}
      />
    </div>
  );
}

export default memo(ZoomRange);
